import React, { useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { isEqual, debounce, compact } from 'lodash';
import { Table, usePrevious } from '@cordata/buffet';
import { toast } from 'react-toastify';
import querystring from 'querystring';
import { formatDate } from '~/utils';

import {
  authorizedInstance as py,
  authorizedRbInstance as rb,
} from '~/utils/api/instances';

const requestData = ({
  pageSize, page = 0, sorted = [], filtered = [],
}, {
  url,
  params,
  instance: i,
}) => {
  const isPython = i === undefined || 'python'.startsWith(i);
  const isRuby = 'ruby'.startsWith(i);

  let instance;

  if (isRuby) instance = rb;
  if (isPython) instance = py;

  let modifiedUrl;

  if (isPython) modifiedUrl = `api/${url}`;

  const parseSort = () => sorted.map((s) => {
    const attrs = s.id.split(',');
    return attrs
      .map(attr => `${s.desc ? '-' : ''}${attr.split('__')[0]}`)
      .join(',');
  });

  return instance
    .get(modifiedUrl, {
      params: {
        'page-size': pageSize,
        page: page + 1,
        sort: parseSort().join(','),
        ...filtered.filter(f => f.value !== null && f.value !== undefined).reduce((a, m) => ({
          ...a,
          [m.id]: m.value instanceof Date ? formatDate(m.value) : m.value,
        }), {}),
        ...params,
      },
      // need to use querystring.stringify to correctly serialize array params
      paramsSerializer: p => querystring.stringify(p),
    })
    .then(r => r.data);
};

const UncontrolledTable = ({
  endpoint,
  dataTransform,
  columns,
  defaultPageSize,
  defaultSorted,
  name,
  ...props
}) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pages, setPages] = useState();
  const [oTotal, setOTotal] = useState(0);
  const [sorted, setSorted] = useState(localStorage.getItem(`${name}-table-state-sorted`) ? JSON.parse(localStorage.getItem(`${name}-table-state-sorted`)) : defaultSorted);
  const [pageSize, setPageSize] = useState(localStorage.getItem(`${name}-table-state-page-size`) || defaultPageSize);
  const prevEndpoint = usePrevious(endpoint);

  useEffect(() => {
    if (!localStorage.getItem(`${name}-table-state-page-size`)) {
      localStorage.setItem(`${name}-table-state-page-size`, defaultPageSize);
    }

    if (!localStorage.getItem(`${name}-table-state-sorted`)) {
      localStorage.setItem(`${name}-table-state-sorted`, JSON.stringify(defaultSorted));
    }
  }, []);

  const fetchData = (state) => {
    setLoading(true);
    setData([]);

    localStorage.setItem(`${name}-table-state-page-size`, state.pageSize);
    setPageSize(state.pageSize);
    localStorage.setItem(`${name}-table-state-sorted`, JSON.stringify(state.sorted));
    setSorted(state.sorted);
    // Save filtered state for CSV exports
    localStorage.setItem(`${name}-table-state-filtered`, JSON.stringify(state.filtered));

    return requestData({ sorted: props.defaultSorted, ...state }, endpoint)
      .then((r) => {
        setData(compact(dataTransform(r.data)));
        setPages(Math.ceil(r.total / (state.pageSize || defaultPageSize)));

        if (oTotal === undefined) {
          setOTotal(r.total);
        }

        return r;
      })
      .catch((e) => {
        console.error(e);
        toast.error('Oops, something went wrong!');
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    setData([]);

    if (prevEndpoint !== undefined && !isEqual(prevEndpoint, endpoint)) {
      fetchData({ sorted, pageSize });
    }
  }, [endpoint]);

  const debouncedFetch = useMemo(() => debounce(
    fetchData,
    750,
  ), [endpoint]);

  return (
    <Table
      minRows={data.length === 0 ? 2 : 0}
      columns={columns}
      data={data}
      pages={pages}
      loading={loading}
      manual
      filterable
      defaultPageSize={pageSize}
      defaultSorted={sorted}
      onFetchData={debouncedFetch}
      {...props}
    />
  );
};

UncontrolledTable.propTypes = {
  endpoint: PropTypes.shape({
    url: PropTypes.string.isRequired,
    params: PropTypes.shape,
  }).isRequired,
  dataTransform: PropTypes.func,
  columns: PropTypes.arrayOf(PropTypes.shape()).isRequired,
  detailLevel: PropTypes.string,
  defaultSorted: PropTypes.arrayOf(PropTypes.shape()),
  showPaginationBottom: PropTypes.bool,
  onClick: PropTypes.func,
  defaultPageSize: PropTypes.number,
  name: PropTypes.string.isRequired,
};

UncontrolledTable.defaultProps = {
  dataTransform: data => data,
  detailLevel: undefined,
  defaultSorted: undefined,
  showPaginationBottom: true,
  onClick: undefined,
  defaultPageSize: 20,
};

export default UncontrolledTable;
